// synced from loomweb blocks/todo-block-view.tsx (de-Tailwind'd).
/**
 * Todo block (TodoWrite). Faithful to loomweb's TodoBlockView: header (icon +
 * "Task List" + completed/total count) and the todo rows (per-status icon,
 * in-progress highlight, completed strike-through). When the block itself is
 * completed, an in-progress todo is shown as pending — same rule as upstream.
 * Tailwind classes are replaced by namespaced `replay-*` CSS; lucide icons by the
 * local inline-SVG set.
 */
import type { JSX } from "react";
import type { TodoBlock, TodoStatus } from "@shared/loom-message-block";
import { ListTodoIcon, CheckIcon, LoaderIcon, CircleIcon } from "./icons";

function TodoStatusIcon({ status }: { status: TodoStatus }): JSX.Element {
  switch (status) {
    case "completed":
      return <CheckIcon className="replay-icon-sm replay-text-success" />;
    case "in_progress":
      return <LoaderIcon className="replay-icon-sm replay-text-primary replay-spin" />;
    case "pending":
    default:
      return <CircleIcon className="replay-icon-sm replay-text-muted" />;
  }
}

export function TodoBlockView({ block }: { block: TodoBlock }): JSX.Element {
  const isBlockCompleted = block.status === "completed";

  const getEffectiveStatus = (status: TodoStatus): TodoStatus => {
    if (isBlockCompleted && status === "in_progress") return "pending";
    return status;
  };

  const completedCount = block.todos.filter((t) => t.status === "completed").length;
  const totalCount = block.todos.length;

  return (
    <div className="replay-block replay-todo-block">
      <div className="replay-block-head">
        <ListTodoIcon className="replay-icon replay-text-primary" />
        <span className="replay-block-label replay-text-secondary">Task List</span>
        <span className="replay-todo-count">
          {completedCount}/{totalCount}
        </span>
      </div>

      <div className="replay-todo-list">
        {block.todos.map((todo, index) => {
          const effectiveStatus = getEffectiveStatus(todo.status);
          const rowClass = [
            "replay-todo-row",
            effectiveStatus === "in_progress" ? "replay-todo-active" : "",
            effectiveStatus === "completed" ? "replay-todo-done" : ""
          ]
            .filter((c) => c.length > 0)
            .join(" ");
          const textClass = [
            "replay-todo-text",
            effectiveStatus === "completed" ? "replay-todo-strike" : ""
          ]
            .filter((c) => c.length > 0)
            .join(" ");
          return (
            <div key={`${todo.content}-${index}`} className={rowClass}>
              <div className="replay-todo-icon">
                <TodoStatusIcon status={effectiveStatus} />
              </div>
              <span className={textClass}>
                {effectiveStatus === "in_progress" && todo.activeForm.length > 0
                  ? todo.activeForm
                  : todo.content}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
